export type Theme = 'light' | 'dark' | 'clean' | 'moderno'

export interface ThemeColors {
  // Backgrounds
  bgPrimary: string
  bgSecondary: string
  bgTertiary: string

  // Text
  fgPrimary: string
  fgSecondary: string
  fgMuted: string

  // Sidebar
  sidebarBg: string
  sidebarFg: string
  sidebarFgMuted: string
  sidebarHover: string
  sidebarActive: string

  // Semantic
  brand: string
  brandHover: string
  brandSoft: string
  brandBorder: string

  accent: string
  accentHover: string
  accentSoft: string
  accentBorder: string

  success: string
  successSoft: string
  successBorder: string

  warning: string
  warningSoft: string
  warningBorder: string

  danger: string
  dangerSoft: string
  dangerBorder: string

  info: string
  infoSoft: string
  infoBorder: string

  border: string
}

export const themes: Record<Theme, ThemeColors> = {
  light: {
    // Tema "Queijo" - quente, vibrante, pizzaria
    bgPrimary: '#f6f1ea',
    bgSecondary: '#ffffff',
    bgTertiary: '#f0ebe3',
    fgPrimary: '#1c1410',
    fgSecondary: '#52525b',
    fgMuted: '#6b6b76',
    sidebarBg: '#0d0c0b',
    sidebarFg: '#e4e4e7',
    sidebarFgMuted: '#a1a1aa',
    sidebarHover: 'rgba(255, 255, 255, 0.05)',
    sidebarActive: 'rgba(255, 255, 255, 0.1)',
    brand: '#dc2626',
    brandHover: '#b91c1c',
    brandSoft: '#fef2f2',
    brandBorder: '#fecaca',
    accent: '#ea580c',
    accentHover: '#c9440a',
    accentSoft: '#fff7ed',
    accentBorder: '#fed7aa',
    success: '#059669',
    successSoft: '#ecfdf5',
    successBorder: '#a7f3d0',
    warning: '#d97706',
    warningSoft: '#fffbeb',
    warningBorder: '#fde68a',
    danger: '#dc2626',
    dangerSoft: '#fef2f2',
    dangerBorder: '#fecaca',
    info: '#2563eb',
    infoSoft: '#eff6ff',
    infoBorder: '#bfdbfe',
    border: '#e4e4e7',
  },
  dark: {
    // Tema "Noite" - mais contrastado, azulado, saturado
    bgPrimary: '#0c0c0f',
    bgSecondary: '#16161c',
    bgTertiary: '#202028',
    fgPrimary: '#f0f0f8',
    fgSecondary: '#b4b4c8',
    fgMuted: '#8d8d9e',
    sidebarBg: '#0c0c0f',
    sidebarFg: '#e4e4e7',
    sidebarFgMuted: '#8888a0',
    sidebarHover: 'rgba(255, 255, 255, 0.08)',
    sidebarActive: 'rgba(255, 255, 255, 0.15)',
    brand: '#f87171',
    brandHover: '#f43f3f',
    brandSoft: '#7f1d1d',
    brandBorder: '#991b1b',
    accent: '#f97316',
    accentHover: '#f97316',
    accentSoft: '#5a2e0c',
    accentBorder: '#92400e',
    success: '#34d399',
    successSoft: '#064e3b',
    successBorder: '#047857',
    warning: '#f59e0b',
    warningSoft: '#542c06',
    warningBorder: '#92400e',
    danger: '#f87171',
    dangerSoft: '#7f1d1d',
    dangerBorder: '#991b1b',
    info: '#60a5fa',
    infoSoft: '#1e3a8a',
    infoBorder: '#1e40af',
    border: '#2a2a38',
  },
  clean: {
    // Tema "Minimalista" - neutro, profissional, AZUL em vez de laranja
    bgPrimary: '#f8f9fa',
    bgSecondary: '#ffffff',
    bgTertiary: '#f1f3f5',
    fgPrimary: '#1a1a2e',
    fgSecondary: '#4a4a6a',
    fgMuted: '#6b6b80',
    sidebarBg: '#1a1a2e',
    sidebarFg: '#e8e8f0',
    sidebarFgMuted: '#9090a8',
    sidebarHover: 'rgba(255, 255, 255, 0.08)',
    sidebarActive: 'rgba(255, 255, 255, 0.12)',
    brand: '#7c3aed',
    brandHover: '#6d28d9',
    brandSoft: '#f5f3ff',
    brandBorder: '#ddd6fe',
    accent: '#2563eb',
    accentHover: '#1d4ed8',
    accentSoft: '#eff6ff',
    accentBorder: '#bfdbfe',
    success: '#16a34a',
    successSoft: '#f0fdf4',
    successBorder: '#bbf7d0',
    warning: '#d97706',
    warningSoft: '#fffbeb',
    warningBorder: '#fde68a',
    danger: '#dc2626',
    dangerSoft: '#fef2f2',
    dangerBorder: '#fecaca',
    info: '#2563eb',
    infoSoft: '#eff6ff',
    infoBorder: '#bfdbfe',
    border: '#e2e4e9',
  },
  moderno: {
    // Tema "Moderno" — neutro frio, tipografia Geist, cantos maiores, sombras
    // suaves e um só acento (laranja-tomate da marca). Ajustes de forma
    // (fonte, raio, sombra, foco) ficam em src/index.css sob
    // [data-theme="moderno"]; aqui só as cores.
    bgPrimary: '#f4f4f5',
    bgSecondary: '#ffffff',
    bgTertiary: '#f0f0f2',
    fgPrimary: '#0b0b0f',
    fgSecondary: '#3f3f46',
    fgMuted: '#6b6b74',
    sidebarBg: '#0b0b0d',
    sidebarFg: '#ececef',
    sidebarFgMuted: '#8b8b94',
    sidebarHover: 'rgba(255, 255, 255, 0.06)',
    sidebarActive: 'rgba(214, 60, 18, 0.22)',
    brand: '#d63c12',
    brandHover: '#b8320e',
    brandSoft: '#fff1ec',
    brandBorder: '#ffd2c2',
    accent: '#d63c12',
    accentHover: '#b8320e',
    accentSoft: '#fff1ec',
    accentBorder: '#ffd2c2',
    success: '#15803d',
    successSoft: '#effaf3',
    successBorder: '#bbf0cd',
    warning: '#a85100',
    warningSoft: '#fff7e8',
    warningBorder: '#fde2ad',
    danger: '#d42020',
    dangerSoft: '#fff1f1',
    dangerBorder: '#fecaca',
    info: '#2563eb',
    infoSoft: '#eff5ff',
    infoBorder: '#c7dafe',
    border: '#e6e6ea',
  },
}

export const THEME_LABELS: Record<Theme, string> = {
  light: 'Queijo',
  dark: 'Noite',
  clean: 'Clean',
  moderno: 'Moderno',
}

export function applyTheme(theme: Theme) {
  const colors = themes[theme]
  const root = document.documentElement

  Object.entries(colors).forEach(([key, value]) => {
    const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`
    root.style.setProperty(cssVar, value)
  })
  // Ajustes que não são cor (fonte, raio, sombra) entram por CSS:
  // [data-theme="moderno"] em src/index.css.
  root.dataset.theme = theme
}
